import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Space,
  Input,
  Form,
  Button,
  notification,
  Row,
  Col,
  Typography,
  Spin,
  Table,
  message,
  Switch,
  Tag,
  Tabs,
  Modal,
  InputNumber,
  Checkbox,
  Dropdown,
} from "antd";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { CopyOutlined, SearchOutlined, ClearOutlined, ExperimentOutlined, FilterOutlined } from "@ant-design/icons";
import moment from "moment";
import RequestViewModal from "../../Request/RequestViewModal";
import { ReactComponent as AiStars } from "../../../assets/svg/ai-creation.svg";
import DatasetRequestEdit from "./DatasetRequestEdit";
import { RequestViewButton } from "../../shared/button/Buttons";

const { TextArea } = Input;
const { Text } = Typography;

// Add interface for tag distribution
interface TagDistribution {
  tag: string;
  count: number;
}

const DatasetEdit: React.FC = () => {
  const { id } = useParams();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);

  // Added requests state
  const [currentAddedPage, setCurrentAddedPage] = useState<number>(1);
  const [addedPageSize, setAddedPageSize] = useState<number>(10);
  const [addedRequests, setAddedRequests] = useState<ChatCompletionRequest[]>(
    []
  );
  const [totalAdded, setTotalAdded] = useState<number>(0);
  const [isAddedLoading, setIsAddedLoading] = useState<boolean>(false);
  const addedRequestsCacheRef = useRef<{
    [page: number]: ChatCompletionRequest[];
  }>({});
  const [selectedAddedRequests, setSelectedAddedRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [showOnlySelectedAdded, setShowOnlySelectedAdded] = useState(false);

  // Eligible requests state
  const [currentEligiblePage, setCurrentEligiblePage] = useState<number>(1);
  const [eligiblePageSize, setEligiblePageSize] = useState<number>(10);
  const [eligibleRequests, setEligibleRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [totalEligible, setTotalEligible] = useState<number>(0);
  const [isEligibleLoading, setIsEligibleLoading] = useState<boolean>(false);
  const eligibleRequestsCacheRef = useRef<{
    [page: number]: ChatCompletionRequest[];
  }>({});
  const [selectedEligibleRequests, setSelectedEligibleRequests] = useState<
    ChatCompletionRequest[]
  >([]);
  const [showOnlySelectedEligible, setShowOnlySelectedEligible] =
    useState(false);

  const [tagDistribution, setTagDistribution] = useState<TagDistribution[]>([]);
  const [showAllTags, setShowAllTags] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("added");
  const [sessionIdFilter, setSessionIdFilter] = useState<string>("");
  const [appliedSessionIdFilter, setAppliedSessionIdFilter] =
    useState<string>("");
  const [queryFilter, setQueryFilter] = useState<string>("");
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ChatCompletionRequest[]>(
    []
  );
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [eligibleRefreshTrigger, setEligibleRefreshTrigger] =
    useState<number>(0);
  const [addedRefreshTrigger, setAddedRefreshTrigger] = useState<number>(0);

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<ChatCompletionRequest | null>(null);

  // Added states for the Added Requests tab search
  const [addedSearchResults, setAddedSearchResults] = useState<
    ChatCompletionRequest[]
  >([]);
  const [isAddedSearchMode, setIsAddedSearchMode] = useState<boolean>(false);
  const [isAddedSearchLoading, setIsAddedSearchLoading] =
    useState<boolean>(false);
  const [addedQueryFilter, setAddedQueryFilter] = useState<string>("");

  // Request type filter state
  const [requestTypeFilters, setRequestTypeFilters] = useState(() => ({
    SYNTHETIC: true,
    PROD: true,
    RERUN: true,
  }));

  // Helper to get selected request types as an array
  const getSelectedRequestTypes = () => {
    return Object.entries(requestTypeFilters)
      .filter(([_, checked]) => checked)
      .map(([type]) => type);
  };

  // Add the missing handler functions
  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  // Handle request updates
  const handleRequestSave = (
    updatedRequest: ChatCompletionRequest,
    table: "eligible" | "added"
  ) => {
    // Update the local state first
    if (table === "eligible") {
      setEligibleRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (eligibleRequestsCacheRef.current[currentEligiblePage]) {
        eligibleRequestsCacheRef.current[currentEligiblePage] =
          eligibleRequestsCacheRef.current[currentEligiblePage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }

      // Also update in search results if in search mode
      if (isSearchMode) {
        setSearchResults((prev) =>
          prev.map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          )
        );
      }
    } else {
      setAddedRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (addedRequestsCacheRef.current[currentAddedPage]) {
        addedRequestsCacheRef.current[currentAddedPage] =
          addedRequestsCacheRef.current[currentAddedPage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }

      // Also update in search results if in search mode
      if (isAddedSearchMode) {
        setAddedSearchResults((prev) =>
          prev.map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          )
        );
      }
    }

    // Show success message
    message.success("Request updated successfully");
  };

  // Fetch dataset details
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      DatasetService.getDataset(Number(id), setIsLoading, apiKey).subscribe(
        (data: FinetuneDataset) => {
          if (data) {
            setDataset(data);
            form.setFieldsValue({
              name: data.name,
              description: data.description,
            });
          } else {
            notification.error({
              message: "Error",
              description: "Failed to fetch dataset: Empty response received",
            });
          }
        },
        (error: any) => {
          console.error("Error fetching dataset:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch dataset: ${error}`,
          });
        }
      );
    }
  }, [id, apiKey, form]);

  // Fetch dataset requests (Added tab)
  useEffect(() => {
    if (id) {
      // Skip fetching if in search mode
      if (isAddedSearchMode) return;

      fetchAddedRequests(currentAddedPage);
    }
  }, [
    currentAddedPage,
    id,
    addedPageSize,
    addedRefreshTrigger,
    isAddedSearchMode,
  ]);

  // Fetch eligible requests (Eligible tab)
  useEffect(() => {
    if (id && activeTab === "eligible" && !isSearchMode) {
      fetchEligibleRequests(currentEligiblePage);
    }
  }, [
    currentEligiblePage,
    id,
    eligiblePageSize,
    activeTab,
    eligibleRefreshTrigger,
    isSearchMode,
  ]);

  // Fetch tag distribution
  useEffect(() => {
    if (id) {
      DatasetService.getDatasetRequestDistribution(id, null, apiKey).subscribe(
        (response: TagDistribution[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch tag distribution: ${error}`,
          });
        }
      );
    }
  }, [id]);

  // Debounced refetch when request type filters or pagination change
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchEligibleRequests(currentEligiblePage);
      fetchAddedRequests(currentAddedPage);
    }, 300); // 0ms ensures it runs after state updates
  }, [requestTypeFilters, currentEligiblePage, eligiblePageSize, currentAddedPage, addedPageSize]);

  // Clear caches when request type filters change to force backend refetch
  useEffect(() => {
    eligibleRequestsCacheRef.current = {};
    addedRequestsCacheRef.current = {};
  }, [requestTypeFilters]);

  const fetchAddedRequests = (page: number) => {
    // If in search mode, don't fetch from the regular endpoint
    if (isAddedSearchMode) {
      return;
    }

    if (addedRequestsCacheRef.current[page]) {
      setAddedRequests(addedRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * addedPageSize;
    setIsAddedLoading(true);

    const types = getSelectedRequestTypes();
    DatasetService.getDatasetRequests(
      Number(id),
      offset,
      addedPageSize,
      setIsAddedLoading,
      apiKey,
      types
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setAddedRequests(response.items);
        setTotalAdded(response.total);
        addedRequestsCacheRef.current = {
          ...addedRequestsCacheRef.current,
          [page]: response.items,
        };
        setIsAddedLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsAddedLoading(false);
      }
    );
  };

  const fetchEligibleRequests = (page: number) => {
    // If in search mode, don't fetch from the regular endpoint
    if (isSearchMode) {
      return;
    }

    if (eligibleRequestsCacheRef.current[page] && !sessionIdFilter) {
      setEligibleRequests(eligibleRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * eligiblePageSize;
    setIsEligibleLoading(true);

    const types = getSelectedRequestTypes();
    DatasetService.getEligibleDatasetRequests(
      Number(id),
      offset,
      eligiblePageSize,
      setIsEligibleLoading,
      apiKey,
      sessionIdFilter,
      types
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setEligibleRequests(response.items);
        setTotalEligible(response.total);

        // Only cache if no session filter is applied
        if (!sessionIdFilter) {
          eligibleRequestsCacheRef.current = {
            ...eligibleRequestsCacheRef.current,
            [page]: response.items,
          };
        }

        setIsEligibleLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch eligible requests: ${error}`,
        });
        setIsEligibleLoading(false);
      }
    );
  };

  const handleSearch = () => {
    // If in search mode, clear it first
    if (isSearchMode) {
      setIsSearchMode(false);
      setSearchResults([]);
    }

    setAppliedSessionIdFilter(sessionIdFilter);
    setCurrentEligiblePage(1);
    eligibleRequestsCacheRef.current = {};
    setEligibleRefreshTrigger((prev) => prev + 1);
  };

  const handleAddSelectedRequests = () => {
    setIsAddedLoading(true);
    setIsEligibleLoading(true);
    const payload = {
      dataset_id: Number(id),
      requests: selectedEligibleRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    DatasetService.addDatasetRequests(
      Number(id),
      payload,
      null,
      apiKey
    ).subscribe(
      () => {
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};

        setSelectedEligibleRequests([]);
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);
        setAddedRefreshTrigger((prev) => prev + 1);
        setEligibleRefreshTrigger((prev) => prev + 1);
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
        message.success(
          `${selectedEligibleRequests.length} requests added successfully`
        );
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to add requests: ${error}`,
        });
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
      }
    );
  };

  const handleRemoveSelectedRequests = () => {
    setIsAddedLoading(true);
    setIsEligibleLoading(true);

    const payload = {
      dataset_id: Number(id),
      requests: selectedAddedRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    DatasetService.removeDatasetRequests(
      Number(id),
      payload,
      null,
      apiKey
    ).subscribe(
      () => {
        // Clear caches regardless of search mode
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};

        // Reset selected requests
        setSelectedAddedRequests([]);

        // Reset to page 1 for both tables
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);

        // Handle refresh based on search state for added requests
        if (isAddedSearchMode && addedQueryFilter.trim()) {
          // Re-execute the search for added requests
          setIsAddedSearchLoading(true);
          DatasetService.searchAddedDatasetRequests(
            Number(id),
            addedQueryFilter,
            null,
            apiKey
          ).subscribe(
            (items: ChatCompletionRequest[]) => {
              setAddedSearchResults(items);
              setAddedRequests(
                items.slice(0, Math.min(addedPageSize, items.length))
              );
              setTotalAdded(items.length);
              setIsAddedSearchLoading(false);
              setIsAddedLoading(false);
            },
            (error: any) => {
              console.error(error);
              notification.error({
                message: "Error",
                description: `Failed to refresh search results: ${error}`,
              });
              setIsAddedSearchLoading(false);
              setIsAddedLoading(false);
            }
          );
        } else {
          // Use default refresh logic for added requests
          setAddedRefreshTrigger((prev) => prev + 1);
          setIsAddedLoading(false);
        }

        // Handle refresh based on search state for eligible requests
        if (activeTab === "eligible" && isSearchMode && queryFilter.trim()) {
          // Re-execute the search for eligible requests
          setIsSearchLoading(true);
          DatasetService.searchEligibleDatasetRequests(
            Number(id),
            queryFilter,
            null,
            apiKey
          ).subscribe(
            (items: ChatCompletionRequest[]) => {
              setSearchResults(items);
              setEligibleRequests(
                items.slice(0, Math.min(eligiblePageSize, items.length))
              );
              setTotalEligible(items.length);
              setIsSearchLoading(false);
              setIsEligibleLoading(false);
            },
            (error: any) => {
              console.error(error);
              notification.error({
                message: "Error",
                description: `Failed to refresh eligible search results: ${error}`,
              });
              setIsSearchLoading(false);
              setIsEligibleLoading(false);
            }
          );
        } else {
          // Use default refresh logic for eligible requests
          setEligibleRefreshTrigger((prev) => prev + 1);
          setIsEligibleLoading(false);
        }

        message.success(
          `${selectedAddedRequests.length} requests removed successfully`
        );
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to remove requests: ${error}`,
        });
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
      }
    );
  };

  const handleQuerySearch = () => {
    if (!queryFilter.trim()) {
      // If empty query, reset to normal mode
      clearSearch();
      return;
    }

    // Clear session filter when doing a query search
    setSessionIdFilter("");
    setAppliedSessionIdFilter("");

    setIsSearchLoading(true);
    setIsSearchMode(true);

    DatasetService.searchEligibleDatasetRequests(
      Number(id),
      queryFilter,
      null,
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        // Preserve selected state for all search results
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedEligibleRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        // Store all search results with preserved selection state
        setSearchResults(updatedItems);

        // Reset to first page when searching
        setCurrentEligiblePage(1);

        // Calculate the items for the current page
        const startIndex = 0;
        const endIndex = Math.min(eligiblePageSize, updatedItems.length);
        setEligibleRequests(updatedItems.slice(startIndex, endIndex));

        setTotalEligible(updatedItems.length);
        setIsSearchLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to search requests: ${error}`,
        });
        setIsSearchLoading(false);
      }
    );
  };

  const clearSearch = () => {
    setQueryFilter("");
    setIsSearchMode(false);
    setSearchResults([]);
    setCurrentEligiblePage(1);
    eligibleRequestsCacheRef.current = {};

    // Also clear session filter if it exists
    if (sessionIdFilter) {
      setSessionIdFilter("");
      setAppliedSessionIdFilter("");
    }

    // Trigger a refresh of eligible requests
    setEligibleRefreshTrigger((prev) => prev + 1);
  };

  // Handle pagination of search results
  const handleSearchPagination = (page: number, pageSize: number) => {
    if (!isSearchMode || searchResults.length === 0) return;

    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, searchResults.length);

    // Get the slice of search results for the current page
    const pageResults = searchResults.slice(startIndex, endIndex);

    // Preserve selected state for items on the current page
    const updatedPageResults = pageResults.map((request) => {
      // Check if this request is in the selectedEligibleRequests
      const selectedVersion = selectedEligibleRequests.find(
        (selected) => selected.request_id === request.request_id
      );
      // If it is, use the selected version, otherwise use the original
      return selectedVersion || request;
    });

    setEligibleRequests(updatedPageResults);
    setCurrentEligiblePage(page);
  };

  const handleAddedSearch = () => {
    if (!addedQueryFilter.trim()) {
      clearAddedSearch();
      return;
    }

    setIsAddedSearchLoading(true);
    setIsAddedSearchMode(true);

    DatasetService.searchAddedDatasetRequests(
      Number(id),
      addedQueryFilter,
      null,
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        // Preserve selected state for all search results
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedAddedRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        // Store all search results with preserved selection state
        setAddedSearchResults(updatedItems);

        // Reset to first page when searching
        setCurrentAddedPage(1);

        // Calculate the items for the current page
        const startIndex = 0;
        const endIndex = Math.min(addedPageSize, updatedItems.length);
        setAddedRequests(updatedItems.slice(startIndex, endIndex));

        setTotalAdded(updatedItems.length);
        setIsAddedSearchLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to search requests: ${error}`,
        });
        setIsAddedSearchLoading(false);
      }
    );
  };

  const clearAddedSearch = () => {
    setAddedQueryFilter("");
    setIsAddedSearchMode(false);
    setAddedSearchResults([]);
    setCurrentAddedPage(1);
    addedRequestsCacheRef.current = {};
    setAddedRefreshTrigger((prev) => prev + 1);
  };

  const handleAddedSearchPagination = (page: number, pageSize: number) => {
    if (!isAddedSearchMode || addedSearchResults.length === 0) return;

    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, addedSearchResults.length);

    // Get the slice of search results for the current page
    const pageResults = addedSearchResults.slice(startIndex, endIndex);

    // Preserve selected state for items on the current page
    const updatedPageResults = pageResults.map((request) => {
      const selectedVersion = selectedAddedRequests.find(
        (selected) => selected.request_id === request.request_id
      );
      return selectedVersion || request;
    });

    setAddedRequests(updatedPageResults);
    setCurrentAddedPage(page);
    setAddedPageSize(pageSize);
  };

  const [isAugmentModalVisible, setIsAugmentModalVisible] = useState(false);
  const [augmentRequestCount, setAugmentRequestCount] = useState<number>(1);
  const [selectedAugmentRequest, setSelectedAugmentRequest] = useState<ChatCompletionRequest | null>(null);
  const [isAugmenting, setIsAugmenting] = useState(false);

  const handleAugmentClick = (request: ChatCompletionRequest) => {
    setSelectedAugmentRequest(request);
    setIsAugmentModalVisible(true);
  };

  const handleAugmentModalOk = () => {
    if (selectedAugmentRequest) {
      try {
        setIsAugmenting(true);
        DatasetService.augmentRequest(
          selectedAugmentRequest.request_id!,
          augmentRequestCount,
          setIsAugmenting,
          apiKey,
          Number(id)
        ).subscribe(
          (response) => {
            // Only expect { status: 'started' }
            notification.success({
              message: "Augmentation Started",
              description: "View progress in the Augmentations tab"
            });
            setIsAugmentModalVisible(false);
            setSelectedAugmentRequest(null);
            setAugmentRequestCount(1);
            setIsAugmenting(false);
            // Navigate back to detail view with augmentations tab
            navigate(`/models/datasets/${id}?tab=augmentations`);
          },
          (error) => {
            console.error("Error augmenting request:", error);
            message.error("Failed to start augmentation");
            setIsAugmenting(false);
          }
        );
      } catch (error) {
        console.error("Error augmenting request:", error);
        message.error("Failed to start augmentation");
        setIsAugmenting(false);
      }
    }
  };

  const handleAugmentModalCancel = () => {
    setIsAugmentModalVisible(false);
    setSelectedAugmentRequest(null);
    setAugmentRequestCount(1);
  };

  const addedColumns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Type",
      key: "request_type",
      width: "10%",
      render: (_: any, record: ChatCompletionRequest) => {
        if (record.request_type === "SYNTHETIC") {
          return <Tag color="blue">Augmented</Tag>;
        } else if (record.request_type === "PROD") {
          return <Tag color="green">Production</Tag>;
        } else if (record.request_type === "RERUN") {
          return <Tag color="volcano">Rerun</Tag>;
        } else {
          return <Tag>Other</Tag>;
        }
      },
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      key: "tags",
      width: "50%",
      render: (_: any, record: ChatCompletionRequest) => {
        try {
          // First check for dataset-specific tags
          let tags: string[] = [];
          if (record.metadata?._maitai_dataset_request_meta) {
            const meta = JSON.parse(
              record.metadata._maitai_dataset_request_meta as string
            );
            tags = meta.tags || [];
          }

          // If no dataset-specific tags, fall back to request metadata tags
          if (tags.length === 0 && record.metadata?._maitai_request_meta) {
            const metaData = JSON.parse(
              record.metadata._maitai_request_meta as string
            );
            tags = metaData.tags || [];
          }

          return (
            <Space wrap>
              {tags.map((tag: string) => (
                <Tag key={tag} bordered={true}>
                  {tag}
                </Tag>
              ))}
              {tags.length === 0 && (
                <span style={{ color: "#999" }}>No tags</span>
              )}
            </Space>
          );
        } catch (e) {
          console.error("Error parsing metadata:", e);
          return <span style={{ color: "#999" }}>No tags</span>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => {
        const requestId = record.request_id || "";
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            {truncatedId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => (
        <Space>
          <Button
            type="link"
            icon={<ExperimentOutlined />}
            onClick={() => handleAugmentClick(record)}>
            Augment
          </Button>
          <RequestViewButton
            apiKey={apiKey}
            request={record}
            onClick={() => handleViewRequest(record)}
            isEditable={false}
            onUpdated={(updatedRequest) =>
              handleRequestSave(updatedRequest, "added")
            }>
            View
          </RequestViewButton>

          <DatasetRequestEdit
            request={record}
            onSave={(updatedRequest) =>
              handleRequestSave(updatedRequest, "added")
            }
            datasetId={id}
            apiKey={apiKey}
          />
        </Space>
      ),
    },
  ];

  const eligibleColumns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Type",
      key: "request_type",
      width: "10%",
      render: (_: any, record: ChatCompletionRequest) => {
        if (record.request_type === "SYNTHETIC") {
          return <Tag color="blue">Augmented</Tag>;
        } else if (record.request_type === "PROD") {
          return <Tag color="green">Production</Tag>;
        } else if (record.request_type === "RERUN") {
          return <Tag color="volcano">Rerun</Tag>;
        } else {
          return <Tag>Other</Tag>;
        }
      },
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      key: "tags",
      width: "50%",
      render: (_: any, record: ChatCompletionRequest) => {
        try {
          if (record.metadata?._maitai_request_meta) {
            const metaData = JSON.parse(
              record.metadata._maitai_request_meta as string
            );
            const tags = metaData.tags || [];
            return (
              <Space wrap>
                {tags.map((tag: string) => (
                  <Tag key={tag} bordered={true}>
                    {tag}
                  </Tag>
                ))}
                {tags.length === 0 && (
                  <span style={{ color: "#999" }}>No tags</span>
                )}
              </Space>
            );
          }
          return <span style={{ color: "#999" }}>No tags</span>;
        } catch (e) {
          console.error("Error parsing metadata:", e);
          return <span style={{ color: "#999" }}>No tags</span>;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => {
        const requestId = record.request_id || "";
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            {truncatedId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: ChatCompletionRequest) => (
        <Space>
          <Button
            type="link"
            icon={<ExperimentOutlined />}
            onClick={() => handleAugmentClick(record)}>
            Augment
          </Button>
          <RequestViewButton
            apiKey={apiKey}
            request={record}
            onClick={() => handleViewRequest(record)}
            isEditable={false}
            onUpdated={(updatedRequest: ChatCompletionRequest) =>
              handleRequestSave(updatedRequest, "eligible")
            }>
            View
          </RequestViewButton>
          <DatasetRequestEdit
            request={record}
            onSave={(updatedRequest) =>
              handleRequestSave(updatedRequest, "eligible")
            }
            isEligible={true}
            onSelect={() => {
              const newRequest = {
                ...record,
                metadata: {
                  ...record.metadata,
                  _maitai_dataset_request_meta: JSON.stringify({ tags: [] }),
                },
              };
              setSelectedEligibleRequests((prev) => {
                const existingIndex = prev.findIndex(
                  (r) => r.request_id === record.request_id
                );
                if (existingIndex >= 0) {
                  // Replace the existing entry
                  const updated = [...prev];
                  updated[existingIndex] = newRequest;
                  return updated;
                }
                // Add as new entry
                return [...prev, newRequest];
              });
            }}
            apiKey={apiKey}
          />
        </Space>
      ),
    },
  ];

  // Styled Dropdown menu for request type filters (vertical, Card background)
  const requestTypeMenu = (
    <Card
      size="small"
      bordered={false}
      style={{
        minWidth: 140,
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        borderRadius: 8,
        padding: 0,
        margin: 0,
      }}
      bodyStyle={{ padding: 8, margin: 0, display: "flex", flexDirection: "column", gap: 0 }}
    >
      <Checkbox
        checked={requestTypeFilters.SYNTHETIC}
        onChange={e => setRequestTypeFilters(f => ({ ...f, SYNTHETIC: e.target.checked }))}
        style={{ margin: 0, padding: "4px 0" }}
      >
        <Tag color="blue" style={{ marginRight: 0, padding: "0 8px" }}>Augmented</Tag>
      </Checkbox>
      <Checkbox
        checked={requestTypeFilters.PROD}
        onChange={e => setRequestTypeFilters(f => ({ ...f, PROD: e.target.checked }))}
        style={{ margin: 0, padding: "4px 0" }}
      >
        <Tag color="green" style={{ marginRight: 0, padding: "0 8px" }}>Production</Tag>
      </Checkbox>
      <Checkbox
        checked={requestTypeFilters.RERUN}
        onChange={e => setRequestTypeFilters(f => ({ ...f, RERUN: e.target.checked }))}
        style={{ margin: 0, padding: "4px 0" }}
      >
        <Tag color="volcano" style={{ marginRight: 0, padding: "0 8px" }}>Rerun</Tag>
      </Checkbox>
    </Card>
  );

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);

      const payload = {
        name: values.name,
        description: values.description,
      };

      DatasetService.updateDataset(
        Number(id),
        payload,
        setIsSaving,
        apiKey
      ).subscribe(
        () => {
          notification.success({
            message: "Success",
            description: "Dataset updated successfully",
          });
          navigate(`/models/datasets/${id}`);
        },
        (error: any) => {
          console.error("Error updating dataset:", error);
          notification.error({
            message: "Error",
            description: `Failed to update dataset: ${error}`,
          });
          setIsSaving(false);
        }
      );
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const items = [
    {
      key: "eligible",
      label: "Eligible Requests",
      children: (
        <>
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "space-between",
              marginBottom: 16,
            }}>
            <div
              style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <Space.Compact style={{ width: "300px" }}>
                  <Input
                    placeholder="Search by query..."
                    value={queryFilter}
                    onChange={(e) => {
                      setQueryFilter(e.target.value);
                      if (e.target.value === "") {
                        clearSearch();
                      }
                    }}
                    onPressEnter={handleQuerySearch}
                    allowClear
                    disabled={isSearchLoading}
                  />
                  <Button
                    type="primary"
                    onClick={handleQuerySearch}
                    loading={isSearchLoading}
                    disabled={!queryFilter.trim()}>
                    <SearchOutlined />
                  </Button>
                </Space.Compact>

                <Space.Compact style={{ width: "300px" }}>
                  <Input
                    placeholder="Search for session..."
                    value={sessionIdFilter}
                    onChange={(e) => {
                      setSessionIdFilter(e.target.value);
                      if (e.target.value === "" && appliedSessionIdFilter) {
                        // If the input is cleared and there was a filter applied
                        setAppliedSessionIdFilter("");
                        setCurrentEligiblePage(1);
                        eligibleRequestsCacheRef.current = {};
                        setEligibleRefreshTrigger((prev) => prev + 1);
                      }
                    }}
                    onPressEnter={handleSearch}
                    allowClear
                    disabled={isSearchMode || isSearchLoading}
                  />
                  <Button
                    type="primary"
                    onClick={handleSearch}
                    disabled={
                      isSearchMode || isSearchLoading || !sessionIdFilter.trim()
                    }>
                    <SearchOutlined />
                  </Button>
                </Space.Compact>

                {(isSearchMode || appliedSessionIdFilter) && (
                  <Button
                    onClick={clearSearch}
                    icon={<ClearOutlined />}
                    disabled={isSearchLoading}>
                    Clear Search
                  </Button>
                )}
              </div>

              {isSearchMode && searchResults.length > 0 && (
                <div style={{ marginTop: "4px" }}>
                  <Tag color="blue">
                    Found {searchResults.length} results for query: "
                    {queryFilter}"
                  </Tag>
                </div>
              )}

              {appliedSessionIdFilter && !isSearchMode && (
                <div style={{ marginTop: "4px" }}>
                  <Tag color="blue">
                    Filtering by session: "{appliedSessionIdFilter}"
                  </Tag>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={showOnlySelectedEligible}
                  onChange={setShowOnlySelectedEligible}
                  style={{ marginRight: 8 }}
                />
                <span>Show Only Selected</span>
              </div>
              {/* Request type filters as dropdown */}
              <Dropdown overlay={requestTypeMenu} trigger={["click"]} placement="bottomRight">
                <Button icon={<FilterOutlined />}>Request Types</Button>
              </Dropdown>
              {selectedEligibleRequests.length > 0 && (
                <Button
                  type="primary"
                  onClick={handleAddSelectedRequests}
                  loading={isEligibleLoading}
                  disabled={isEligibleLoading}>
                  Add Selected ({selectedEligibleRequests.length})
                </Button>
              )}
            </div>
          </div>
          <Table
            rowKey="request_id"
            columns={eligibleColumns}
            dataSource={
              showOnlySelectedEligible
                ? selectedEligibleRequests
                : eligibleRequests
            }
            loading={isEligibleLoading || isSearchLoading}
            pagination={{
              current: currentEligiblePage,
              pageSize: eligiblePageSize,
              total: showOnlySelectedEligible
                ? selectedEligibleRequests.length
                : isSearchMode
                ? searchResults.length
                : totalEligible,
              onChange: (page, pageSize) => {
                if (isSearchMode) {
                  // In search mode, handle pagination differently
                  handleSearchPagination(page, pageSize || eligiblePageSize);
                  return;
                }

                setCurrentEligiblePage(page);
                if (pageSize && pageSize !== eligiblePageSize) {
                  setEligiblePageSize(pageSize);
                  setEligibleRequests([]);
                  eligibleRequestsCacheRef.current = {};
                }
              },
              showSizeChanger: !isSearchMode,
              pageSizeOptions: ["10", "20", "50", "100"],
              onShowSizeChange: (current, size) => {
                if (isSearchMode) return;

                setCurrentEligiblePage(1);
                setEligiblePageSize(size);
                setEligibleRequests([]);
                eligibleRequestsCacheRef.current = {};
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedEligibleRequests.map(
                (r) => r.request_id
              ),
              onChange: (selectedRowKeys, selectedRows) => {
                // Get the request IDs of the currently visible rows
                const visibleRequestIds = new Set(
                  eligibleRequests.map((r) => r.request_id)
                );

                // Keep previously selected rows that are not on the current page
                const previouslySelectedRowsFromOtherPages =
                  selectedEligibleRequests.filter(
                    (row) => !visibleRequestIds.has(row.request_id)
                  );

                // Combine with newly selected rows from current page
                setSelectedEligibleRequests([
                  ...previouslySelectedRowsFromOtherPages,
                  ...selectedRows,
                ]);

                // If in search mode, update the searchResults to reflect selection state
                if (isSearchMode) {
                  const allSelectedRequestIds = new Set([
                    ...previouslySelectedRowsFromOtherPages.map(
                      (r) => r.request_id
                    ),
                    ...selectedRows.map((r) => r.request_id),
                  ]);

                  // Update searchResults to reflect the new selection state
                  setSearchResults((prevSearchResults) =>
                    prevSearchResults.map((result) => {
                      // If this result is now selected
                      if (allSelectedRequestIds.has(result.request_id)) {
                        // If it's on the current page, use the version from selectedRows
                        if (visibleRequestIds.has(result.request_id)) {
                          const selectedVersion = selectedRows.find(
                            (r) => r.request_id === result.request_id
                          );
                          return selectedVersion || result;
                        }
                        // If it's from another page, use the version from previouslySelectedRowsFromOtherPages
                        const selectedVersion =
                          previouslySelectedRowsFromOtherPages.find(
                            (r) => r.request_id === result.request_id
                          );
                        return selectedVersion || result;
                      }

                      // If not selected, return the original
                      return result;
                    })
                  );

                  // Also update the current page of eligibleRequests
                  const startIndex =
                    (currentEligiblePage - 1) * eligiblePageSize;
                  const endIndex = Math.min(
                    startIndex + eligiblePageSize,
                    searchResults.length
                  );
                  setEligibleRequests(
                    searchResults.slice(startIndex, endIndex).map((result) => {
                      if (
                        allSelectedRequestIds.has(result.request_id) &&
                        visibleRequestIds.has(result.request_id)
                      ) {
                        const selectedVersion = selectedRows.find(
                          (r) => r.request_id === result.request_id
                        );
                        return selectedVersion || result;
                      }
                      return result;
                    })
                  );
                }
              },
            }}
          />
        </>
      ),
    },
    {
      key: "added",
      label: "Added Requests",
      children: (
        <>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <div
              style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <Space.Compact style={{ width: "300px" }}>
                  <Input
                    placeholder="Search by query..."
                    value={addedQueryFilter}
                    onChange={(e) => {
                      setAddedQueryFilter(e.target.value);
                      if (e.target.value === "") {
                        clearAddedSearch();
                      }
                    }}
                    onPressEnter={handleAddedSearch}
                    allowClear
                    disabled={isAddedSearchLoading}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddedSearch}
                    loading={isAddedSearchLoading}
                    disabled={!addedQueryFilter.trim()}>
                    <SearchOutlined />
                  </Button>
                </Space.Compact>

                {isAddedSearchMode && (
                  <Button
                    onClick={clearAddedSearch}
                    icon={<ClearOutlined />}
                    disabled={isAddedSearchLoading}>
                    Clear Search
                  </Button>
                )}
              </div>

              {isAddedSearchMode && addedSearchResults.length > 0 && (
                <div style={{ marginTop: "4px" }}>
                  <Tag color="blue">
                    Found {addedSearchResults.length} results for query: "
                    {addedQueryFilter}"
                  </Tag>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={showOnlySelectedAdded}
                  onChange={setShowOnlySelectedAdded}
                  style={{ marginRight: 8 }}
                />
                <span>Show Only Selected</span>
              </div>
              {/* Request type filters as dropdown */}
              <Dropdown overlay={requestTypeMenu} trigger={["click"]} placement="bottomRight">
                <Button icon={<FilterOutlined />}>Request Types</Button>
              </Dropdown>
              {selectedAddedRequests.length > 0 && (
                <Button
                  type="primary"
                  danger
                  onClick={handleRemoveSelectedRequests}
                  loading={isAddedLoading}
                  disabled={isAddedLoading}>
                  Remove Selected ({selectedAddedRequests.length})
                </Button>
              )}
            </div>
          </div>
          <Table
            rowKey="request_id"
            columns={addedColumns}
            dataSource={
              showOnlySelectedAdded
                ? selectedAddedRequests
                : addedRequests
            }
            loading={isAddedLoading || isAddedSearchLoading}
            pagination={{
              current: currentAddedPage,
              pageSize: addedPageSize,
              total: showOnlySelectedAdded
                ? selectedAddedRequests.length
                : isAddedSearchMode
                ? addedSearchResults.length
                : totalAdded,
              onChange: (page, pageSize) => {
                if (isAddedSearchMode) {
                  handleAddedSearchPagination(page, pageSize || addedPageSize);
                  return;
                }

                setCurrentAddedPage(page);
                if (pageSize && pageSize !== addedPageSize) {
                  setAddedPageSize(pageSize);
                  setAddedRequests([]);
                  addedRequestsCacheRef.current = {};
                }
              },
              showSizeChanger: !isAddedSearchMode,
              pageSizeOptions: ["10", "20", "50", "100"],
              onShowSizeChange: (current, size) => {
                if (isAddedSearchMode) {
                  handleAddedSearchPagination(current, size);
                  return;
                }

                setCurrentAddedPage(1);
                setAddedPageSize(size);
                setAddedRequests([]);
                addedRequestsCacheRef.current = {};
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedAddedRequests.map((r) => r.request_id),
              onChange: (selectedRowKeys, selectedRows) => {
                // Get the request IDs of the currently visible rows
                const visibleRequestIds = new Set(
                  addedRequests.map((r) => r.request_id)
                );

                // Keep previously selected rows that are not on the current page
                const previouslySelectedRowsFromOtherPages =
                  selectedAddedRequests.filter(
                    (row) => !visibleRequestIds.has(row.request_id)
                  );

                // Combine with newly selected rows from current page
                setSelectedAddedRequests([
                  ...previouslySelectedRowsFromOtherPages,
                  ...selectedRows,
                ]);

                // If in search mode, update the addedSearchResults to reflect selection state
                if (isAddedSearchMode) {
                  const allSelectedRequestIds = new Set([
                    ...previouslySelectedRowsFromOtherPages.map(
                      (r) => r.request_id
                    ),
                    ...selectedRows.map((r) => r.request_id),
                  ]);

                  // Update addedSearchResults to reflect the new selection state
                  setAddedSearchResults((prevSearchResults) =>
                    prevSearchResults.map((result) => {
                      // If this result is now selected
                      if (allSelectedRequestIds.has(result.request_id)) {
                        // If it's on the current page, use the version from selectedRows
                        if (visibleRequestIds.has(result.request_id)) {
                          const selectedVersion = selectedRows.find(
                            (r) => r.request_id === result.request_id
                          );
                          return selectedVersion || result;
                        }
                        // If it's from another page, use the version from previouslySelectedRowsFromOtherPages
                        const selectedVersion =
                          previouslySelectedRowsFromOtherPages.find(
                            (r) => r.request_id === result.request_id
                          );
                        return selectedVersion || result;
                      }

                      // If not selected, return the original
                      return result;
                    })
                  );

                  // Also update the current page of addedRequests
                  const startIndex = (currentAddedPage - 1) * addedPageSize;
                  const endIndex = Math.min(
                    startIndex + addedPageSize,
                    addedSearchResults.length
                  );
                  setAddedRequests(
                    addedSearchResults
                      .slice(startIndex, endIndex)
                      .map((result) => {
                        if (
                          allSelectedRequestIds.has(result.request_id) &&
                          visibleRequestIds.has(result.request_id)
                        ) {
                          const selectedVersion = selectedRows.find(
                            (r) => r.request_id === result.request_id
                          );
                          return selectedVersion || result;
                        }
                        return result;
                      })
                  );
                }
              },
            }}
          />
        </>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div
          style={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>
          {!dataset ? (
            <Spin size="small" />
          ) : (
            <>
              <Link to={"/models/datasets"} style={{ color: "#828282" }}>
                Datasets
                {" / "}
              </Link>
              <Link to={`/models/datasets/${id}`} style={{ color: "#828282" }}>
                {dataset.name}
                {" / "}
              </Link>
              Edit
            </>
          )}
        </div>

        {dataset && (
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: "16px" }}>
                Details
              </Text>
            }
            style={{
              background: "#f8f9fa",
              borderRadius: "8px",
              marginBottom: "12px",
            }}
            loading={isLoading}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary">Source</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {dataset.intent_group_name &&
                    !dataset.application_id &&
                    !dataset.intent_id ? (
                      <Link to={`/intent-groups/${dataset.intent_group_id}`}>
                        {dataset.intent_group_name}
                      </Link>
                    ) : dataset.application_name &&
                      dataset.intent_name &&
                      !dataset.intent_group_id ? (
                      <>
                        <Link to={`/application/${dataset.application_id}`}>
                          {dataset.application_name}
                        </Link>
                        {" / "}
                        <Link
                          to={`/application/${dataset.application_id}/intent/${dataset.intent_id}`}>
                          {dataset.intent_name}
                        </Link>
                      </>
                    ) : (
                      "Unknown source"
                    )}
                  </Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">Date Created</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {moment(dataset.date_created).format(
                      "MMM D, YYYY HH:mm:ss"
                    )}
                  </Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">Total Requests</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {dataset.request_count || 0}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        )}

        <Form form={form} layout="vertical" disabled={isLoading}>
          <div
            style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
            <Form.Item
              name="name"
              label={<span style={{ fontWeight: 600 }}>Name</span>}
              rules={[{ required: true, message: "Please enter a name" }]}
              style={{ flex: 1 }}>
              <Input placeholder="Enter dataset name" />
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label={<span style={{ fontWeight: 600 }}>Description</span>}>
            <TextArea placeholder="Enter dataset description" rows={4} />
          </Form.Item>

          {tagDistribution.length > 0 && (
            <Form.Item
              label={
                <span style={{ fontWeight: 600 }}>Request Distribution</span>
              }>
              <Space wrap>
                {(showAllTags
                  ? tagDistribution
                  : tagDistribution.slice(0, 20)
                ).map(({ tag, count }) => (
                  <Tag key={tag} bordered={true}>
                    {tag} ({count})
                  </Tag>
                ))}
                {tagDistribution.length > 20 && !showAllTags && (
                  <Button
                    type="link"
                    onClick={() => setShowAllTags(true)}
                    style={{ padding: "0 8px" }}>
                    Show All ({tagDistribution.length})
                  </Button>
                )}
                {showAllTags && tagDistribution.length > 20 && (
                  <Button
                    type="link"
                    onClick={() => setShowAllTags(false)}
                    style={{ padding: "0 8px" }}>
                    Show Less
                  </Button>
                )}
              </Space>
            </Form.Item>
          )}
        </Form>

        <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "24px",
          }}>
          <Button
            onClick={() => navigate(`/models/datasets/${id}`)}
            disabled={isSaving}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave} loading={isSaving}>
            Save Changes
          </Button>
        </div>
      </Space>

      <Modal
        title="Augment Request"
        open={isAugmentModalVisible}
        onOk={handleAugmentModalOk}
        onCancel={handleAugmentModalCancel}
        okText="Augment"
        cancelText="Cancel"
        confirmLoading={isAugmenting}>
        <div>
          <Text>
            Augmenting this request will create semantically similar variations while preserving the original intent to enhance dataset diversity. These requests can then be added to your dataset from the Augmentations tab.<br /><br />
          </Text>
        </div>
        <Form layout="vertical">
          <Form.Item
            label="Number of requests to create"
            help="Enter a number between 1 and 100">
            <InputNumber
              min={1}
              max={100}
              step={1}
              value={augmentRequestCount}
              onChange={(value) => setAugmentRequestCount(value || 1)}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          apiKey={apiKey}
        />
      )}
    </Card>
  );
};

export default DatasetEdit;
